import React from 'react';
import { motion } from 'framer-motion';
import { useAnimateOnScroll } from '../hooks';

const clearances = [
  { type: 'CERT', name: 'AWS Certified Solutions Architect - Associate' },
  { type: 'CERT', name: 'Hyperledger Fabric Fundamentals' },
  { type: 'CERT', name: 'Blockchain Foundation Program' },
  { type: 'DOMAIN', name: 'Government Data Centers (Air-Gapped)' },
  { type: 'DOMAIN', name: 'High-Traffic FinTech & eCommerce' }
];

const About: React.FC = () => {
  const { ref, inView } = useAnimateOnScroll();

  return (
    <section id="about" className="py-16 sm:py-20 bg-[#050505] px-4 sm:px-8 md:px-12 border-t border-zinc-900 overflow-hidden">
      <div className="max-w-6xl mx-auto" ref={ref}>
        
        {/* Terminal Font Heading */}
        <div className="mb-10 sm:mb-14">
          <h2 className="text-xl sm:text-3xl md:text-4xl font-mono font-medium text-white uppercase tracking-tight mb-2">
            ~/System_Profile
          </h2>
          <div className="text-zinc-500 font-mono text-xs sm:text-sm tracking-widest uppercase">
            01 // Identity, Telemetry & Clearances
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          
          {/* Left Column: Narrative & Clearances */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 flex flex-col justify-between"
          >
            <div>
              <h3 className="text-lg sm:text-2xl md:text-3xl font-mono font-medium text-white uppercase tracking-tight mb-6 leading-snug">
                <span className="text-zinc-600 select-none mr-2">{'>'}</span>
                Bridging high-performance AI workloads with zero-trust security environments.
              </h3>
              
              <div className="text-zinc-400 font-sans leading-relaxed text-sm sm:text-base mb-10 space-y-4">
                <p>
                  Currently engineering VMware Tanzu and OpenShift architectures within highly restricted, air-gapped government data centers. 
                </p>
                <p>
                  My expertise lies in stripping away manual infrastructure dependencies, enforcing rigid GitOps state reconciliation, and optimizing NVIDIA GPU orchestration to ensure resilient, production-grade AI inference.
                </p>
              </div>
            </div>
            
            {/* Expanded "Wow Factor" Certifications Block */}
            <div className="border border-zinc-800/80 bg-[#0a0a0a] relative group mt-auto">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-zinc-400"></div>
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-zinc-400"></div>
              
              <div className="border-b border-zinc-800/80 p-3 sm:p-4 bg-zinc-900/30 flex justify-between items-center">
                <h4 className="text-zinc-400 font-mono text-[10px] sm:text-xs tracking-widest uppercase">
                  Authorizations & Validated Protocols
                </h4>
                <div className="w-2 h-2 bg-white animate-pulse"></div>
              </div>
              
              <div className="p-4 sm:p-5 font-mono">
                <ul className="space-y-3">
                  {clearances.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 group-hover:bg-zinc-900/40 p-1 transition-colors">
                      <span className={`shrink-0 px-1.5 py-0.5 text-[9px] tracking-widest uppercase mt-0.5 ${item.type === 'CERT' ? 'bg-zinc-800 text-zinc-300' : 'border border-zinc-700 text-zinc-400'}`}>
                        {item.type}
                      </span>
                      <span className="text-zinc-300 leading-tight text-xs sm:text-sm">
                        {item.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Balanced Live Telemetry Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5 grid grid-cols-2 gap-4 sm:gap-6 content-start"
          >
            
            {/* Metric 1 */}
            <div className="bg-[#0a0a0a] border border-zinc-800/80 p-4 sm:p-5 flex flex-col justify-between group hover:border-zinc-500 transition-colors col-span-2 sm:col-span-1">
              <span className="font-mono text-zinc-500 text-[10px] uppercase tracking-widest mb-4 flex justify-between">
                <span>Tenure</span>
                <span className="text-zinc-700">{'//'}</span>
              </span>
              <div>
                <span className="font-mono text-3xl font-medium text-white tracking-tighter">
                  6.0+
                </span>
                <span className="font-mono text-zinc-500 text-xs ml-1">YRS</span>
              </div>
              <div className="w-full h-1 bg-zinc-900 mt-4 flex">
                <div className="w-[85%] h-full bg-zinc-400"></div>
              </div>
            </div>

            {/* Metric 2 */}
            <div className="bg-[#0a0a0a] border border-zinc-800/80 p-4 sm:p-5 flex flex-col justify-between group hover:border-zinc-500 transition-colors col-span-2 sm:col-span-1">
              <span className="font-mono text-zinc-500 text-[10px] uppercase tracking-widest mb-4 flex justify-between">
                <span>Peak Load</span>
                <span className="text-zinc-700">{'//'}</span>
              </span>
              <div>
                <span className="font-mono text-3xl font-medium text-white tracking-tighter">
                  20K+
                </span>
                <span className="font-mono text-zinc-500 text-xs ml-1">USR</span>
              </div>
              <div className="flex items-end gap-[2px] mt-4 h-3 opacity-60 group-hover:opacity-100 transition-opacity">
                <div className="w-full bg-zinc-500 h-[40%]"></div>
                <div className="w-full bg-zinc-500 h-[70%]"></div>
                <div className="w-full bg-zinc-500 h-[50%]"></div>
                <div className="w-full bg-zinc-500 h-[90%]"></div>
                <div className="w-full bg-zinc-500 h-[60%]"></div>
                <div className="w-full bg-white h-[100%]"></div>
              </div>
            </div>

            {/* Metric 3 - Full Width */}
            <div className="bg-[#0a0a0a] border border-zinc-800/80 p-4 sm:p-5 flex flex-col group hover:border-zinc-500 transition-colors col-span-2">
              <span className="font-mono text-zinc-500 text-[10px] uppercase tracking-widest mb-2 flex justify-between">
                <span>Migrations Executed</span>
                <span className="text-zinc-700">{'//'}</span>
              </span>
              <div className="flex items-end gap-2">
                <span className="font-mono text-3xl font-medium text-white tracking-tighter">
                  30+
                </span>
                <span className="font-mono text-zinc-400 text-xs sm:text-sm pb-1">Zero-Downtime</span>
              </div>
            </div>

            {/* Metric 4 - Full Width */}
            <div className="bg-[#0a0a0a] border border-zinc-800/80 p-4 sm:p-5 flex flex-col group hover:border-zinc-500 transition-colors col-span-2">
              <span className="font-mono text-zinc-500 text-[10px] uppercase tracking-widest mb-2 flex justify-between">
                <span>Primary Architecture</span>
                <span className="text-zinc-700">{'//'}</span>
              </span>
              <div className="flex flex-wrap gap-2 mt-2">
                 <span className="font-mono text-[10px] sm:text-xs bg-zinc-900/80 border border-zinc-700 text-zinc-300 px-2 py-1 uppercase">HPC Compute</span>
                 <span className="font-mono text-[10px] sm:text-xs bg-zinc-900/80 border border-zinc-700 text-zinc-300 px-2 py-1 uppercase">Air-Gapped</span>
                 <span className="font-mono text-[10px] sm:text-xs bg-zinc-900/80 border border-zinc-700 text-zinc-300 px-2 py-1 uppercase">Bare-Metal K8s</span>
              </div>
            </div>

          </motion.div>
          
        </div>
      </div>
    </section>
  );
};

export default About;